import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Check, Repeat, CircleDot, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTOMATE_LABELS = {
  n8n: { label: 'n8n-automatisering', icon: Zap, color: 'bg-purple-100 text-purple-700' },
  lovable: { label: 'In Lovable', icon: null, color: 'bg-blue-100 text-blue-700' },
  handmatig: { label: 'Handmatig', icon: null, color: 'bg-muted text-muted-foreground' },
};

const STATUS_LABELS = {
  proposed: { label: 'Voorgesteld', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Goedgekeurd', color: 'bg-green-100 text-green-700' },
  scheduled: { label: 'Actief', color: 'bg-green-100 text-green-700' },
  done: { label: 'Klaar', color: 'bg-muted text-muted-foreground' },
  skipped: { label: 'Overgeslagen', color: 'bg-muted text-muted-foreground' },
};

function ItemCard({ item, checked, onToggle }) {
  const auto = AUTOMATE_LABELS[item.automate_with] || AUTOMATE_LABELS.handmatig;
  const st = STATUS_LABELS[item.status] || STATUS_LABELS.proposed;
  const AutoIcon = auto.icon;
  const selectable = item.status === 'proposed';
  return (
    <div className="flex items-start gap-3 p-4 border rounded-lg bg-card">
      {selectable ? (
        <Checkbox checked={checked} onCheckedChange={() => onToggle(item.id)} className="mt-1"
                  data-testid={`action-item-check-${item.id}`} />
      ) : (
        <div className="w-4 h-4 mt-1 flex items-center justify-center">
          <Check className="w-4 h-4 text-green-600" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground">{item.title}</p>
          <Badge variant="secondary" className={`text-xs ${st.color}`}>{st.label}</Badge>
        </div>
        {item.detail && <p className="text-sm text-muted-foreground mt-1">{item.detail}</p>}
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${auto.color}`}>
            {AutoIcon && <AutoIcon className="w-3 h-3" />}
            {auto.label}
          </span>
          {item.cadence && <span className="inline-flex items-center gap-1"><Repeat className="w-3 h-3" />{item.cadence}</span>}
          <span className="text-muted-foreground">via {item.source_agent}</span>
          {item.assignee && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
              → {item.assignee}
            </span>
          )}
        </div>
        {item.last_error && <p className="text-xs text-red-600 mt-1">{item.last_error}</p>}
      </div>
    </div>
  );
}

export default function ActionPlanView({ projectId, activationStatus, onApproved }) {
  const [data, setData] = useState({ one_time: [], recurring: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/action-items`);
      setData(res.data);
      // preselect all proposed items
      const proposed = [...(res.data.one_time || []), ...(res.data.recurring || [])]
        .filter((i) => i.status === 'proposed').map((i) => i.id);
      setSelected(new Set(proposed));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [projectId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const proposedCount = useMemo(
    () => [...data.one_time, ...data.recurring].filter((i) => i.status === 'proposed').length,
    [data]
  );

  const approve = async () => {
    if (selected.size === 0) { toast.error('Selecteer minstens één actie'); return; }
    setApproving(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/action-items/approve`, {
        item_ids: [...selected],
      });
      toast.success(`${res.data.approved} acties goedgekeurd${res.data.queued_for_n8n ? `, ${res.data.queued_for_n8n} naar n8n` : ''}`);
      await fetchItems();
      onApproved && onApproved();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Goedkeuren mislukt');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const empty = data.one_time.length === 0 && data.recurring.length === 0;
  if (empty) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center text-muted-foreground">
          Nog geen plan. Draai eerst de activatie-pipeline op het Overzicht-tabblad.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="action-plan">
      {proposedCount > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div>
            <p className="text-sm font-medium text-foreground">{proposedCount} acties wachten op jouw goedkeuring</p>
            <p className="text-xs text-muted-foreground">Niets draait voordat jij op goedkeuren klikt. {selected.size} geselecteerd.</p>
          </div>
          <Button onClick={approve} disabled={approving} data-testid="approve-plan-btn">
            {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Keur {selected.size} goed
          </Button>
        </div>
      )}

      {data.recurring.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Repeat className="w-4 h-4" /> Terugkerend</CardTitle>
            <CardDescription>Blijft draaien — uptime, digests, content-concepten, LLM-checks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.recurring.map((i) => (
              <ItemCard key={i.id} item={i} checked={selected.has(i.id)} onToggle={toggle} />
            ))}
          </CardContent>
        </Card>
      )}

      {data.one_time.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CircleDot className="w-4 h-4" /> Eenmalig</CardTitle>
            <CardDescription>Fixes en verbeteringen die je één keer doorvoert</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.one_time.map((i) => (
              <ItemCard key={i.id} item={i} checked={selected.has(i.id)} onToggle={toggle} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
